<template>
  <div :class="['form-element', hidden && 'd-none']">
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id
      }"
    />
    <Multiselect
      :id="id"
      ref="multiselect"
      :value="value"
      :customLabel="customLabel"
      :placeholder="disabled ? '—' : `Type to search for ${ $localize('program', 'plural') }`"
      :disabled="disabled"
      open-direction="bottom"
      :options="options"
      :loading="isLoading"
      :internal-search="false"
      :limit-text="limitText"
      :show-no-options="true"
      :show-no-results="true"
      :multiple="config.multiple"
      class="mb-4 mt-2"
      @input="update"
      @search-change="asyncFind"
    >
      <template slot="noOptions">Search to find {{ $localize('program', 'plural') }}.</template>

      <template slot="noResult">No {{ $localize('program', 'plural') }} found.</template>
    </Multiselect>
  </div>
</template>

<script>
import { inject } from '@vue/composition-api'
import FormMixin from '@/components/Forms/FormMixin'
import { mapGetters } from 'vuex'
import { getCmProgramsSearch } from '@/api'
import _debounce from 'lodash/debounce'
import Multiselect from '@/components/Multiselect'
import { DEBOUNCE_INTERVAL } from '@/constants/'
import FormLabel from '@/components/Forms/FormLabel.vue'
import useProgram from '@cm/client/composables/useProgram'

export default {
  mixins: [ FormMixin ],
  components: { Multiselect, FormLabel },
  props: {
    config: {
      type: Object,
      default: () => ({
        multiple: true,
        departments: [],
        returnFields: [ 'id', 'programGroupId', 'name', 'longName', 'transcriptDescription', 'code', 'description', 'departments', 'requestStatus', 'status' ],
      }),
    },
  },
  data () {
    return {
      programsData: [],
      isLoading: false,
      searchQuery: null,
    }
  },
  setup () {
    const effectiveDatesRange = inject('effectiveDatesRange', '')
    const trackByGroupId = inject('trackByGroupId', false)

    const {
      generateProgramLabel,
    } = useProgram()
    return {
      generateProgramLabel,
      trackByGroupId,
      effectiveDatesRange,
    }
  },
  computed: {
    ...mapGetters([ 'school' ]),
    ...mapGetters('curriculumPrograms', [ 'cmProgramById', 'cmProgramByEffectiveDatesRangeLookup' ]),
    searchConfig () {
      const config = {
        school: this.school,
        searchQuery: this.searchQuery,
        returnArray: true,
        includePending: true,
        effectiveDatesRange: this.config.effectiveDatesRange || this.effectiveDatesRange,
        ...this.config.returnLatestRevision ? { returnLatestRevision: this.config.returnLatestRevision } : {},
        ...this.config.useFetchingRequisitesLatestRevisionSettings
          ? { useFetchingRequisitesLatestRevisionSettings: this.config.useFetchingRequisitesLatestRevisionSettings }
          : {},
      }
      if (this.config.departments && this.config.departments.length) {
        config.departments = this.config.departments.join(',')
      }
      return config
    },
    options () {
      return this.programsData.map(program => program[this.trackAttribute])
    },
    trackAttribute () {
      return this.trackByGroupId ? 'programGroupId' : 'id'
    },
  },
  created () {
    const existingValues = this.value || []
    let valuesToLoad = []
    if (Array.isArray(existingValues)) {
      valuesToLoad = existingValues.filter(el => !this.getProgramFromStore(el))
    } else if (!this.getProgramFromStore(existingValues)) {
      valuesToLoad = [ existingValues ]
    }
    if (valuesToLoad.length === 0) return {}
    const payload = this.trackByGroupId
      ? {
        programGroupIds: valuesToLoad,
        effectiveDatesRange: this.config.effectiveDatesRange || this.effectiveDatesRange,
        ...this.config.returnLatestRevision ? { returnLatestRevision: this.config.returnLatestRevision } : {},
        ...this.config.useFetchingRequisitesLatestRevisionSettings
          ? { useFetchingRequisitesLatestRevisionSettings: this.config.useFetchingRequisitesLatestRevisionSettings }
          : {},
      }
      : { list: valuesToLoad }
    return this.$store.dispatch('curriculumPrograms/getPrograms', payload)
  },
  methods: {
    getProgram (id) {
      return this.programsData.find(c => c[this.trackAttribute] === id)
    },
    getProgramFromStore (id) {
      return this.trackByGroupId
        ? this.cmProgramByEffectiveDatesRangeLookup(
          id,
          this.config.effectiveDatesRange || this.effectiveDatesRange,
          'programGroupId',
          this.config.useFetchingRequisitesLatestRevisionSettings || this.config.returnLatestRevision,
        )
        : this.cmProgramById(id)
    },
    limitText (count) {
      return `and ${count} other programs`
    },
    customLabel (value) {
      const program = this.getProgram(value) || this.getProgramFromStore(value)
      return this.generateProgramLabel(program)
    },
    setProgram (value) {
      const program = this.getProgram(value)
      if (program) this.$store.commit('curriculumPrograms/setCmProgram', program)
    },
    update (value) {
      if (Array.isArray(value)) {
        value.forEach(this.setProgram)
      } else {
        this.setProgram(value)
      }
      this.$emit('input', value)
    },
    asyncFind (query) {
      if (query) {
        this.isLoading = true
        this.getProgramsDebounced(query)
      } else {
        this.isLoading = false
        this.programsData = []
      }
    },
    getProgramsDebounced: _debounce(function (query) {
      this.getPrograms(query)
    }, DEBOUNCE_INTERVAL),
    getPrograms (query) {
      this.searchQuery = query
      getCmProgramsSearch(this.searchConfig).then(data => {
        this.programsData = data ? data.data : []
        this.isLoading = false
      })
    },
  },
}
</script>

<style scoped>
.form-control {
  height: 36px;
}
.multiselect >>> .multiselect__tag {
  white-space: normal;
}
.multiselect >>> .multiselect__tags {
  overflow: hidden;
}
</style>
